import React from "react";
import { Star, Sparkles } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { translations } from "../i18n/translations";
import { images } from "../data/images";

export default function Rates() {
  const { t } = useLanguage();
  const tx = translations.rates;

  return (
    <section id="tarifas" className="relative bg-[#EDE6D7] py-24 lg:py-36 overflow-hidden">
      {/* Background accent */}
      <div
        className="absolute right-0 top-0 w-1/2 h-full opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: `url(${images.heroAerial1})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12">
        {/* Heading */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[1px] w-10 bg-[#9C8456]" />
            <span
              className="text-[#9C8456] text-[11px] tracking-[0.45em] uppercase"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {t(tx.eyebrow)}
            </span>
            <div className="h-[1px] w-10 bg-[#9C8456]" />
          </div>
          <h2
            className="text-[#2A2A28] text-[34px] sm:text-[48px] lg:text-[58px] leading-[1.08] max-w-3xl mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t(tx.title1)} <em className="italic">{t(tx.title2)}</em>
          </h2>
          <p
            className="text-[#3a3a36] text-[15px] lg:text-[17px] leading-[1.85] mt-7 max-w-2xl mx-auto"
            style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
          >
            {t(tx.intro)}
          </p>
        </div>

        {/* Seasons grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-20">
          {tx.seasons.map((s, i) => (
            <div
              key={i}
              className={`relative bg-[#FFFCF6] border p-7 lg:p-9 flex flex-col transition-all duration-500 hover:translate-y-[-4px] ${
                s.featured
                  ? "border-[#9C8456] shadow-lg"
                  : "border-[#D4CBB8]/40 hover:border-[#9C8456]/60"
              }`}
            >
              {s.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#9C8456] text-white px-5 py-1.5 flex items-center gap-1.5">
                  <Star className="w-3 h-3" strokeWidth={1.6} fill="currentColor" />
                  <span
                    className="text-[9px] tracking-[0.3em] uppercase"
                    style={{ fontFamily: "'Jost', sans-serif", fontWeight: 500 }}
                  >
                    {t({ en: "Most popular", es: "Más popular" })}
                  </span>
                </div>
              )}

              <p
                className="text-[#9C8456] text-[10px] tracking-[0.4em] uppercase mb-3"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(s.season)}
              </p>
              <h3
                className="text-[#2A2A28] text-[20px] lg:text-[22px] leading-tight mb-5 min-h-[55px]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                {t(s.period)}
              </h3>

              <div className="h-[1px] bg-[#E0DACE] mb-5" />

              <div className="flex items-baseline gap-1 mb-3">
                <span
                  className="text-[#2A2A28] text-[44px] lg:text-[52px] leading-none"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                >
                  ${s.price.toLocaleString()}
                </span>
                <span
                  className="text-[#9C8456] text-[13px]"
                  style={{ fontFamily: "'Jost', sans-serif", fontWeight: 400 }}
                >
                  USD
                </span>
              </div>
              <p
                className="text-[#6B6760] text-[12px] tracking-[0.15em] uppercase mb-6"
                style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
              >
                {t(tx.perNight)}
              </p>

              <p
                className="text-[#3a3a36] text-[12px] tracking-[0.18em] uppercase mt-auto pt-4 border-t border-[#E0DACE]"
                style={{ fontFamily: "'Jost', sans-serif", fontWeight: 400 }}
              >
                {t(s.minNights)}
              </p>
            </div>
          ))}
        </div>

        {/* Add-ons + Discounts */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
          {/* Add-ons */}
          <div className="bg-[#FFFCF6] border border-[#E0DACE] p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-7">
              <Sparkles className="w-4 h-4 text-[#9C8456]" strokeWidth={1.4} />
              <h3
                className="text-[#9C8456] text-[10px] tracking-[0.4em] uppercase"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(tx.addonsTitle)}
              </h3>
            </div>

            <div className="space-y-6">
              {tx.addons.map((a, i) => (
                <div
                  key={i}
                  className="flex items-start justify-between gap-4 pb-6 border-b border-[#E0DACE] last:border-0 last:pb-0"
                >
                  <div className="flex-1">
                    <h4
                      className="text-[#2A2A28] text-[20px] lg:text-[22px] leading-tight mb-1.5"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                      {t(a.name)}
                    </h4>
                    <p
                      className="text-[#6B6760] text-[13px] leading-[1.55]"
                      style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
                    >
                      {t(a.desc)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p
                      className="text-[#2A2A28] text-[24px] lg:text-[28px] leading-none"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                      ${a.price.toLocaleString()}
                      <span className="text-[12px] text-[#9C8456] ml-1">USD</span>
                    </p>
                    <p
                      className="text-[#9C8456] text-[10px] tracking-[0.25em] uppercase mt-1"
                      style={{ fontFamily: "'Jost', sans-serif" }}
                    >
                      {t(a.unit)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Discounts */}
          <div className="bg-[#2A2A28] text-[#F5F0E8] p-8 lg:p-10 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage: `url(${images.heroAerial2})`,
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            />
            <div className="relative">
              <h3
                className="text-[#C9B789] text-[10px] tracking-[0.4em] uppercase mb-7"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(tx.discountsTitle)}
              </h3>

              <div className="space-y-7">
                {tx.discounts.map((d, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-6 pb-7 border-b border-[#3a3a36] last:border-0 last:pb-0"
                  >
                    <div
                      className="flex-shrink-0 w-20 h-20 lg:w-24 lg:h-24 rounded-full border border-[#9C8456]/60 flex flex-col items-center justify-center"
                    >
                      <span
                        className="text-[#C9B789] text-[24px] lg:text-[28px] leading-none"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                      >
                        {d.value}
                      </span>
                      <span
                        className="text-[#9C8456] text-[8px] tracking-[0.25em] uppercase mt-1"
                        style={{ fontFamily: "'Jost', sans-serif" }}
                      >
                        {t({ en: "OFF", es: "DSCTO" })}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h4
                        className="text-[#F5F0E8] text-[20px] lg:text-[24px] leading-tight mb-1"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                      >
                        {t(d.name)}
                      </h4>
                      <p
                        className="text-[#C9B789]/80 text-[13px] leading-[1.55]"
                        style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
                      >
                        {t(d.desc)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Note + CTA */}
        <div className="text-center max-w-3xl mx-auto">
          <p
            className="text-[#6B6760] text-[13px] leading-[1.7] mb-7 italic"
            style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
          >
            {t(tx.note)}
          </p>
          <a
            href="#contacto"
            className="inline-flex items-center justify-center px-10 py-4 text-[11px] tracking-[0.3em] uppercase bg-[#2A2A28] text-[#F5F0E8] hover:bg-[#9C8456] transition-colors duration-300"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            {t(tx.cta)}
          </a>
        </div>
      </div>
    </section>
  );
}
